import React, { useEffect, useState } from 'react';
import Modal from '@components/Modals';
import cn from 'classnames';
import Header from '@components/Modals/Header';
import styles from './LinkedInCSVModal.module.scss';
import DragDropFileOpener, { FileLoadedIcon } from '@components/DragAndDropFile/DragAndDropFile';
import type { ParseResult } from 'papaparse';
import Papa from 'papaparse';
import { apiRequestWithRetry } from '@support/api';
import { CircledCheckIcon, CircledXIcon, LoaderIcon } from '@components/Icons';
import Action from '@components/Button/Action';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllIntegrations } from '@actions/integrations';
import { IntegrationListResponse } from '@models/Integration';
import { selectIntegrationByPlatform } from '@reducers/user/integrations';

interface LinkedInConnectionsCSVRecord {
  'First Name': string;
  'Last Name': string;
  'URL': string;
  'Email Address': string;
  'Company': string;
  'Position': string;
  'Connected On': string;
}

interface LinkedInConnectionRecord {
  // taken from the URL
  slug: string;
  // first + last name
  name: string;
  headline: string;
  score: number;
}

async function openAndParseCSVFile(file: File): Promise<ParseResult<LinkedInConnectionsCSVRecord>> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', (ev) => {
      if (ev.target?.result) {
        // this is to remove Notes from the beginning of the file
        let data = ev.target.result as string;
        const headers = 'First Name,Last Name,URL,Email Address,Company,Position,Connected On';
        const index = data.indexOf(headers);
        data = data.substring(index);

        const parsed = Papa.parse<LinkedInConnectionsCSVRecord>(data, {
          header: true,
        });

        resolve(parsed);
      }
      reject('Failed to parse file.');
    });
    reader.readAsText(file);
  });
}

enum ImportUXState {
  initial,
  parsing,
  ready,
  submitting,
  done,
  error,
}

export default function LinkedInCSVModal({ onClose, forceDarkMode = false }) {
  const [rawFile, setRawFile] = React.useState<File>();
  const [formattedRecords, setFormattedRecords] = React.useState<LinkedInConnectionRecord[]>([]);
  const [state, setState] = useState<ImportUXState>(ImportUXState.initial);
  const linkedInIntegration = useSelector(selectIntegrationByPlatform('linkedin'));
  const dispatch = useDispatch();

  useEffect(() => {
    if (!rawFile) {
      // setParsedCSV(undefined);
      setState(ImportUXState.initial);
      return void 0;
    }

    const abort = new AbortController();
    setState(ImportUXState.parsing);
    (async () => {
      let parsed;
      try {
        parsed = await openAndParseCSVFile(rawFile);
      } catch (e) {
        console.error(e);
        setState(ImportUXState.error);
        return;
      }
      if (abort.signal.aborted) {
        return;
      }

      const time = Math.round(Date.now() / 1000);
      const records = parsed.data
        .filter((row) => 'URL' in row && !!row['URL'].trim())
        .map((row, index) => {
          return {
            slug: row['URL'].replace('https://www.linkedin.com/in/', ''),
            name: `${row['First Name']} ${row['Last Name']}`.trim(),
            headline: row['Position']?.trim(),
            score: time - index,
          };
        });
      // hard cap at 30k, in case someone is cheating the system
      setFormattedRecords(records.slice(0, 30000));
      // setParsedCSV(parsed);
      setState(ImportUXState.ready);
    })();

    return () => {
      abort.abort();
    };
  }, [rawFile]);

  async function onSubmit() {
    if (state !== ImportUXState.ready || !formattedRecords.length) {
      return;
    }

    setState(ImportUXState.submitting);

    // check if we have an integration already
    let integrationId =
      !!linkedInIntegration && !linkedInIntegration.isFetching ? linkedInIntegration.id : undefined;

    const size = 1000;
    const chunks = Array.from({ length: Math.ceil(formattedRecords.length / size) }, (_, index) =>
      formattedRecords.slice(index * size, index * size + size),
    );

    for (const chunk of chunks) {
      try {
        const URL = !!integrationId ? `/integrations/${integrationId}/` : '/integrations/linkedin/';
        const method = !!integrationId ? 'PATCH' : 'POST';

        const result = await apiRequestWithRetry<IntegrationListResponse>(
          method,
          URL,
          {
            items: chunk,
          },
          {
            apiVersion: 'v2',
          },
        );
        // so future calls will update the integration instead of creating a new one
        integrationId = result.id;
      } catch (e) {
        console.error(e);
        setState(ImportUXState.error);
        return;
      }
    }

    setState(ImportUXState.done);
    dispatch(fetchAllIntegrations());
  }

  return (
    <Modal
      className={cn(styles.csvModal, 'modal-medium')}
      forceDarkMode={forceDarkMode}
      centered
      isOpen={true}
      onRequestClose={onClose}
    >
      <Header>LinkedIn CSV Import</Header>
      <div className={styles.csvContent}>
        <p>
          <a href="https://www.linkedin.com/mypreferences/d/download-my-data" target="_blank">
            Request a data export from LinkedIn
          </a>
          . Do a custom export and select "Connections".
        </p>
        <p>
          Once LinkedIn sends you an email that your export is ready, head back to that same page to
          download the CSV file.
        </p>
        {state === ImportUXState.initial && (
          <DragDropFileOpener
            onFile={setRawFile}
            text={
              <>
                Drag & drop the "Connections.csv" file<br />or click here to search for it
              </>
            }
          />
        )}
        {(state === ImportUXState.parsing || state === ImportUXState.submitting) && (
          <div className={cn(styles.box, styles.loading)}>
              <LoaderIcon width={24} height={24} />
              {state === ImportUXState.parsing && 'Loading CSV file...'}
              {state === ImportUXState.submitting && 'Submitting data...'}
          </div>
        )}
        {state === ImportUXState.ready && (
            <div className={cn(styles.box, styles.ready)}>
                <FileLoadedIcon />
                Ready to import {formattedRecords.length} records.
            </div>
        )}
        {state === ImportUXState.done && (
            <div className={styles.box}>
                <CircledCheckIcon width="24" height="24" color="primary" />
                Done. You will soon start to see<br/>these connections in Clay.
              </div>
        )}
        {state === ImportUXState.error && (
            <div className={cn(styles.box, styles.error)}>
                <CircledXIcon width="24" height="24" customColor="#EB515B" />
                  Something went wrong. Please try again.<br/>If the issue persists, please contact
                  support.
            </div>
        )}
        <div className={styles.actions}>
          {state !== ImportUXState.done && state !== ImportUXState.error ? (
            <>
              <Action onClick={onClose} secondary forceDarkMode={forceDarkMode}>
                Cancel
              </Action>
              <Action
                variant="primary"
                onClick={onSubmit}
                disabled={state !== ImportUXState.ready || formattedRecords.length === 0}
                forceDarkMode={forceDarkMode}
              >
                Import
              </Action>
            </>
          ) : (
            <>
              <Action onClick={onClose} block forceDarkMode={forceDarkMode}>
                Close
              </Action>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
